import {
  Component, ElementRef, EventEmitter, Input, Output, ViewChild,
  OnChanges, SimpleChanges
} from '@angular/core';
import { FractionRound } from '../../../interfaces/fraction-line.interface';

interface Sparkle { x: number; y: number; r: number; delay: number; }
interface RevealCell { x: number; w: number; filled: boolean; index: number; }

/**
 * Fraction Blast play surface — a cosmic 0–1 number line with a rocket
 * on a track below. The child drags the rocket to aim (a live beam +
 * marker shows where it will strike the line), then `launch()` fires it
 * straight up. A hit within the round's tolerance plants a glowing star
 * and celebrates; a miss is silent (re-aim). `hintVisible` reveals the
 * denominator's tick subdivisions so they can count.
 *
 * Presentation-only, like the other skill stages: it owns the aim
 * gesture + launch animation and reports the outcome via
 * `solved` / `missed`. Scoring / turns live in the shell + service.
 */
@Component({
  selector: 'app-fraction-line-stage',
  templateUrl: './fraction-line-stage.component.html',
  styleUrls: ['./fraction-line-stage.component.scss']
})
export class FractionLineStageComponent implements OnChanges {
  @Input() round: FractionRound | null = null;
  @Input() roundKey = 0;
  @Input() hintVisible = false;
  @Input() interactive = true;
  @Input() currLang: any = 'en';

  @Output() solved = new EventEmitter<void>();
  @Output() missed = new EventEmitter<void>();

  @ViewChild('svg', { static: true }) svgRef!: ElementRef<SVGSVGElement>;

  readonly VIEW_W = 1000;
  readonly VIEW_H = 560;
  readonly X0 = 130;          // number-line left (value 0)
  readonly X1 = 870;          // number-line right (value 1)
  readonly LINE_Y = 235;      // number-line y
  readonly TRACK_Y = 430;     // rocket resting y

  readonly BAR_Y = 189;       // fraction-reveal bar (above the line)
  readonly BAR_H = 26;

  rocketX = 130;
  rocketY = 430;
  launching = false;
  solvedState = false;
  missPuff: { x: number; y: number } | null = null;
  planted: { x: number } | null = null;
  sparkles: Sparkle[] = [];

  /** Red feedback for a wrong launch: shows what the child *did* — the
   *  length from 0 to where they launched (`x`) — NOT where the answer
   *  is (that would gift them the answer). Cleared when they re-aim. */
  guessResult: { x: number } | null = null;
  wrongFlash = false;

  /** Answer reveal: line split into `denominator` equal parts, first
   *  `numerator` filled — the fraction shown as a length + a point. */
  revealed = false;
  revealCells: RevealCell[] = [];
  revealTicks: number[] = [];

  private dragging = false;
  private grabDx = 0;
  private timers: any[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['round'] || changes['roundKey']) this.reset();
  }

  private reset(): void {
    this.clearTimers();
    this.launching = false;
    this.solvedState = false;
    this.guessResult = null;
    this.wrongFlash = false;
    this.missPuff = null;
    this.planted = null;
    this.sparkles = [];
    this.revealed = false;
    this.revealCells = [];
    this.revealTicks = [];
    this.dragging = false;
    // Start the rocket a bit in from the left so it's clearly "not the
    // answer" and there's room to slide both ways.
    this.rocketX = this.X0 + (this.X1 - this.X0) * 0.12;
    this.rocketY = this.TRACK_Y;
  }

  // ── Geometry helpers ───────────────────────────────────────────────

  private get span(): number { return this.X1 - this.X0; }
  get guessValue(): number { return (this.rocketX - this.X0) / this.span; }
  get targetX(): number { return this.round ? this.X0 + this.round.value * this.span : this.X0; }
  get rocketTransform(): string { return `translate(${this.rocketX}, ${this.rocketY})`; }

  /** Interior hint ticks dividing 0–1 into `denominator` equal parts. */
  get hintTicks(): number[] {
    if (!this.round) return [];
    const d = this.round.denominator;
    const out: number[] = [];
    for (let i = 1; i < d; i++) out.push(this.X0 + (i / d) * this.span);
    return out;
  }

  // ── Aim (drag the rocket) ──────────────────────────────────────────

  onRocketDown(evt: PointerEvent): void {
    if (!this.interactive || this.launching || this.solvedState) return;
    evt.preventDefault();
    evt.stopPropagation();
    const p = this.toSvg(evt);
    this.dragging = true;
    this.grabDx = p.x - this.rocketX;
    // Grabbing to re-aim clears the previous wrong-guess feedback.
    this.missPuff = null;
    this.guessResult = null;
    try { this.svgRef.nativeElement.setPointerCapture(evt.pointerId); } catch { /* older TVs */ }
  }

  onPointerMove(evt: PointerEvent): void {
    if (!this.dragging) return;
    const p = this.toSvg(evt);
    this.rocketX = Math.max(this.X0, Math.min(this.X1, p.x - this.grabDx));
  }

  onPointerUp(evt: PointerEvent): void {
    if (!this.dragging) return;
    this.dragging = false;
    try { this.svgRef.nativeElement.releasePointerCapture(evt.pointerId); } catch { /* ignore */ }
  }

  get canLaunch(): boolean {
    return this.interactive && !this.launching && !this.solvedState;
  }

  // ── Launch (called by the shell's LAUNCH button) ───────────────────

  launch(): void {
    if (!this.canLaunch || !this.round) return;
    const hit = Math.abs(this.guessValue - this.round.value) <= this.round.tolerance;
    const landedX = this.rocketX;
    this.launching = true;
    this.missPuff = null;
    this.guessResult = null;
    // Fly the rocket up to the line (CSS-transitioned transform).
    this.rocketY = this.LINE_Y;

    this.timers.push(setTimeout(() => {
      if (hit) {
        // Correct: reveal the fraction as its partitioned length + point.
        this.planted = { x: this.targetX };
        this.solvedState = true;
        this.buildReveal();
        this.spawnSparkles(this.targetX);
        this.solved.emit();
      } else {
        // Wrong: show what THEY did — the length from 0 to where they
        // launched, in red — NOT where the answer is. Then re-aim.
        this.guessResult = { x: landedX };
        this.missPuff = { x: landedX, y: this.LINE_Y };
        this.wrongFlash = true;
        this.timers.push(setTimeout(() => { this.wrongFlash = false; }, 620));
        this.missed.emit();
        this.timers.push(setTimeout(() => {
          this.rocketY = this.TRACK_Y;   // drop back so they can re-aim
          this.launching = false;
        }, 900));
      }
    }, 560));
  }

  /** Split 0–1 into `denominator` equal cells and mark the first
   *  `numerator` as filled — the fraction as a shaded length whose
   *  right edge lands exactly on the answer point. */
  private buildReveal(): void {
    if (!this.round) return;
    const d = this.round.denominator;
    const n = this.round.numerator;
    const cellW = this.span / d;
    this.revealCells = Array.from({ length: d }, (_, i) => ({
      x: this.X0 + i * cellW, w: cellW, filled: i < n, index: i
    }));
    this.revealTicks = Array.from({ length: d + 1 }, (_, i) => this.X0 + i * cellW);
    this.revealed = true;
  }

  private spawnSparkles(cx: number): void {
    this.sparkles = Array.from({ length: 12 }, (_, i) => {
      const a = (i / 12) * Math.PI * 2;
      const dist = 30 + (i % 3) * 18;
      return { x: cx + Math.cos(a) * dist, y: this.LINE_Y + Math.sin(a) * dist, r: 4 + (i % 3) * 3, delay: (i % 5) * 35 };
    });
  }

  private toSvg(evt: PointerEvent): { x: number; y: number } {
    const svg = this.svgRef.nativeElement;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const m = pt.matrixTransform(ctm.inverse());
    return { x: m.x, y: m.y };
  }

  private clearTimers(): void {
    this.timers.forEach(t => clearTimeout(t));
    this.timers = [];
  }
}
